import { Warning } from '@phosphor-icons/react';
import { useEffect, useRef, useState } from 'react';

import { useAuth } from '@/lib/auth/AuthContext';
import { useApiClient } from '@/lib/api/useApiClient';
import { Button } from '@/ui/button';

import { FullPageLoader } from '@/app/shell/FullPageLoader';

/**
 * Shown once a user is authenticated through a social provider but has no
 * partner_id yet (no Partner provisioned). Calls the backend to find-or-create
 * the sandbox Partner and link the Keycloak user, then refreshes the session so
 * the new token carries partner_id and the shell can render.
 */
export function CompleteSetupGate() {
    const apiClient = useApiClient();
    const { signIn, signOut } = useAuth();
    const [failed, setFailed] = useState(false);
    const started = useRef(false);

    useEffect(() => {
        if (started.current) return;
        started.current = true;
        void (async () => {
            try {
                await apiClient.post('/auth/complete-setup');
                // Re-issue the token through the active SSO session so it carries
                // partner_id (signinSilent uses an iframe Keycloak blocks via
                // X-Frame-Options; a redirect reuses the session seamlessly).
                signIn();
            } catch {
                setFailed(true);
            }
        })();
    }, [apiClient, signIn]);

    if (!failed) return <FullPageLoader />;

    return (
        <main className="flex min-h-screen items-center justify-center bg-background px-4">
            <div className="flex w-full max-w-sm flex-col items-center gap-4 text-center">
                <Warning weight="regular" className="h-8 w-8 text-destructive" />
                <p className="text-sm text-muted-foreground">
                    We couldn&apos;t finish setting up your account. Please try again.
                </p>
                <Button variant="outline" size="sm" onClick={() => signOut()}>
                    Back to sign in
                </Button>
            </div>
        </main>
    );
}
